import type { AriaAttributes } from 'react';

import type { Color } from '#/theme/theme';
import { colors } from '#/theme/theme';

import {
  MaskImageIconDiv,
  maskImageIconSrcUrlVar,
} from './MaskImageIcon.styled';

type Props = {
  src: string;
  size: number;
  opacity: number | undefined;
  colorName: Color | undefined;
  qaId: string | undefined;
  alt: string;
} & AriaAttributes;

export function MaskImageIcon({
  src,
  size,
  opacity,
  colorName,
  qaId,
  alt,
  ...ariaProps
}: Props) {
  const {
    'aria-label': ariaLabelFromProps,
    'aria-hidden': ariaHidden,
    ...restAriaProps
  } = ariaProps;

  // NOTE: we're checking explicitly for these values as they need to be unset
  // in some parent components, e.g. the InputField error message, where the
  // label conflicts with screen readers. In those components, setting
  // aria-label={undefined} would be a noop, getting overridden by this component.
  const shouldHide = ariaHidden === true || ariaHidden === 'true';
  let ariaLabel: string | undefined;
  if ('aria-label' in ariaProps) {
    ariaLabel = ariaLabelFromProps;
  } else if (shouldHide) {
    ariaLabel = undefined;
  } else {
    ariaLabel = alt;
  }

  return (
    <MaskImageIconDiv
      data-qa-id={qaId}
      // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
      role="img"
      aria-label={ariaLabel}
      aria-hidden={ariaHidden}
      // oxlint-disable-next-line react/jsx-props-no-spreading
      {...restAriaProps}
      style={{
        width: size,
        height: size,
        opacity,
        color: colorName ? colors[colorName] : undefined,
        [maskImageIconSrcUrlVar]: `url(${src})`,
      }}
    />
  );
}
